/**
 * Converters between the legacy "marker string" format and HTML.
 *
 * Legacy markers (still produced by old saved templates):
 *   **text**   -> <strong>text</strong>
 *   &&text&&   -> <em>text</em>
 *   __text__   -> <u>text</u>
 *   {{Field}}  -> placeholder chip (preserved as-is in HTML)
 *
 * Newlines are turned into paragraph / line breaks so TipTap can edit them
 * naturally, and we always output well-formed HTML on save.
 */

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

/** Turn the legacy `**` `&&` `__` `{{}}` markers into HTML tags. */
const markersToHtmlInline = (text: string): string => {
  let out = ''
  let bold = false
  let italic = false
  let underline = false
  let i = 0

  const open = (tag: string) => `<${tag}>`
  const close = (tag: string) => `</${tag}>`

  let buffer = ''
  const flushBuffer = () => {
    if (!buffer) return
    out += escapeHtml(buffer).replace(/\{\{(.*?)\}\}/g, (_m, p1) => {
      const safe = escapeHtml(p1).replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
      return `<span data-placeholder="${safe}" class="rte-placeholder">{{${safe}}}</span>`
    })
    buffer = ''
  }

  while (i < text.length) {
    const two = text.substr(i, 2)
    if (two === '**') {
      flushBuffer()
      out += bold ? close('strong') : open('strong')
      bold = !bold
      i += 2
    } else if (two === '&&') {
      flushBuffer()
      out += italic ? close('em') : open('em')
      italic = !italic
      i += 2
    } else if (two === '__') {
      flushBuffer()
      out += underline ? close('u') : open('u')
      underline = !underline
      i += 2
    } else {
      buffer += text[i]
      i += 1
    }
  }
  flushBuffer()
  if (underline) out += close('u')
  if (italic) out += close('em')
  if (bold) out += close('strong')
  return out
}

/** Detect whether a stored value is already HTML (new format) or legacy markers. */
export const looksLikeHtml = (s?: string | null): boolean => {
  if (!s) return false
  // Anything that contains a block-level tag or a span we produced counts as HTML.
  return /<\s*(p|div|h[1-6]|ul|ol|li|br|span|strong|em|u|s|a|hr|blockquote)\b/i.test(s)
}

/**
 * Convert a legacy marker string to HTML suitable for TipTap.
 * We split on blank lines into paragraphs; single newlines become `<br>`.
 */
export const markersToHtml = (raw: string): string => {
  if (!raw) return '<p></p>'
  if (looksLikeHtml(raw)) return raw

  // Normalize line endings.
  const text = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

  // Split into paragraphs on 1+ blank lines, then on single newlines render as <br>.
  const paragraphs = text.split(/\n{2,}/)
  return paragraphs
    .map((p) => {
      const lines = p.split('\n').map(markersToHtmlInline)
      return `<p>${lines.join('<br>')}</p>`
    })
    .join('')
}

/**
 * Convert HTML produced by the editor back to the legacy marker format used by
 * the existing jsPDF generator. Used only as a fallback when the backend
 * cannot render HTML directly. Preserves bold/italic/underline + placeholders;
 * font color/size are intentionally dropped because the legacy renderer cannot
 * express them.
 */
export const htmlToMarkers = (html: string): string => {
  if (!html) return ''
  if (typeof window === 'undefined') return html

  const container = document.createElement('div')
  container.innerHTML = html

  const visit = (node: Node, ctx: { bold: boolean; italic: boolean; underline: boolean }): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      // Use textContent so HTML entities are decoded.
      return (node.textContent || '').replace(/\u00a0/g, ' ')
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return ''
    const el = node as HTMLElement
    const tag = el.tagName.toLowerCase()

    if (tag === 'br') return '\n'

    // Placeholder chip we inserted: emit as {{Field}}.
    if (el.getAttribute('data-placeholder')) {
      const key = el.getAttribute('data-placeholder') || ''
      return `{{${key}}}`
    }

    // Determine new style context from tag and inline styles.
    const style = el.getAttribute('style') || ''
    const isBoldTag = tag === 'strong' || tag === 'b' || /font-weight\s*:\s*(bold|[6-9]00)/i.test(style)
    const isItalicTag = tag === 'em' || tag === 'i' || /font-style\s*:\s*italic/i.test(style)
    const isUnderlineTag = tag === 'u' || /text-decoration[^;]*underline/i.test(style)

    const newCtx = {
      bold: ctx.bold || isBoldTag,
      italic: ctx.italic || isItalicTag,
      underline: ctx.underline || isUnderlineTag,
    }

    let inner = ''
    el.childNodes.forEach((c) => (inner += visit(c, newCtx)))

    // Wrap with markers iff this element actually toggles the style.
    if (isBoldTag && !ctx.bold) inner = `**${inner}**`
    if (isItalicTag && !ctx.italic) inner = `&&${inner}&&`
    if (isUnderlineTag && !ctx.underline) inner = `__${inner}__`

    if (tag === 'p' || tag === 'div' || /^h[1-6]$/.test(tag)) inner = inner + '\n\n'
    if (tag === 'li') inner = `\u2022 ${inner}\n`

    return inner
  }

  let out = ''
  container.childNodes.forEach((n) => (out += visit(n, { bold: false, italic: false, underline: false })))

  return out.replace(/\n{3,}/g, '\n\n').trimEnd()
}

/* ------------------------------------------------------------------ *
 * Inline HTML
 * ------------------------------------------------------------------ */

/**
 * Tags we keep as-is when flattening to inline HTML.
 * Anything else is unwrapped (its children are kept) or treated as a block.
 */
const INLINE_TAGS = new Set(['strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'span', 'a', 'code', 'sub', 'sup'])

/** Tags that end a visual line when flattened. */
const BLOCK_TAGS = new Set([
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'section',
  'article',
  'li',
])

/** Style properties worth carrying into an email. Everything else is dropped. */
const ALLOWED_STYLE_PROPS = new Set([
  'color',
  'background-color',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-decoration',
])

const filterStyle = (style: string): string =>
  style
    .split(';')
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => ALLOWED_STYLE_PROPS.has(decl.split(':')[0]?.trim().toLowerCase() || ''))
    .join('; ')

/** Only allow link targets that are safe to put in an email. */
const safeHref = (href: string): string =>
  /^(https?:|mailto:|tel:|#|\/)/i.test(href.trim()) ? href.trim() : ''

/**
 * Flatten editor HTML into INLINE-only HTML.
 *
 * The email element renderers in `utils/emailHtml.ts` interpolate these values
 * inside `<h1>` / `<h2>` / `<h4>` / `<span>`, where a block tag such as `<p>`
 * is invalid nesting and renders unpredictably across mail clients. So block
 * boundaries become `<br/>` and only inline marks survive.
 *
 * Bold, italic, underline, strikethrough, colour, font family, font size,
 * links, list bullets and line breaks are preserved. Block text-align is
 * intentionally dropped: alignment is the element TYPE's job (element2 is
 * centered, element11 is left), not the text's.
 */
export const htmlToInlineHtml = (html: string): string => {
  if (!html) return ''
  if (typeof document === 'undefined') return html

  const container = document.createElement('div')
  container.innerHTML = html

  const renderChildren = (node: Node, depth: number): string => {
    let out = ''
    node.childNodes.forEach((child) => {
      out += renderNode(child, depth)
    })
    return out
  }

  const renderNode = (node: Node, depth: number): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      // textContent is already decoded, so re-escape it.
      return escapeHtml(node.textContent || '').replace(/ /g, '&nbsp;')
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return ''

    const el = node as HTMLElement
    const tag = el.tagName.toLowerCase()

    if (tag === 'br') return '<br/>'
    if (tag === 'hr') return '<br/>'
    if (tag === 'script' || tag === 'style') return ''

    // Placeholder chips serialize back to the `{{Field}}` text the
    // string-replacement renderers expect.
    const placeholderName = el.getAttribute('data-placeholder')
    if (placeholderName) return `{{${placeholderName}}}`

    // Mention chips serialize back to `@Name`, which is what the note's
    // save-time reconciliation matches on.
    if (el.hasAttribute('data-mention')) return `@${el.getAttribute('data-mention-name') || ''}`

    if (tag === 'ul' || tag === 'ol') {
      const items: string[] = []
      let index = 0
      el.childNodes.forEach((child) => {
        if (child.nodeType !== Node.ELEMENT_NODE) return
        if ((child as HTMLElement).tagName.toLowerCase() !== 'li') return
        index += 1
        const marker = tag === 'ol' ? `${index}. ` : '• '
        items.push(marker + renderChildren(child, depth + 1).replace(/(<br\/>)+$/, ''))
      })
      return items.length ? items.join('<br/>') + '<br/>' : ''
    }

    if (INLINE_TAGS.has(tag)) {
      const inner = renderChildren(el, depth + 1)
      if (!inner) return ''
      const attrs: string[] = []
      if (tag === 'a') {
        const href = safeHref(el.getAttribute('href') || '')
        if (!href) return inner
        attrs.push(`href="${escapeHtml(href)}"`, 'target="_blank"', 'rel="noopener noreferrer"')
      }
      const style = filterStyle(el.getAttribute('style') || '')
      if (style) attrs.push(`style="${escapeHtml(style)}"`)
      return `<${tag}${attrs.length ? ' ' + attrs.join(' ') : ''}>${inner}</${tag}>`
    }

    if (BLOCK_TAGS.has(tag)) {
      const inner = renderChildren(el, depth + 1)
      if (!inner.replace(/<br\/>/g, '').trim()) return '<br/>'
      return `${inner}<br/>`
    }

    // Unknown tag: keep its text content, drop the tag.
    return renderChildren(el, depth + 1)
  }

  return renderChildren(container, 0)
    .replace(/(<br\/>){3,}/g, '<br/><br/>')
    .replace(/(<br\/>)+$/, '')
}
